import { useQueryClient } from '@tanstack/react-query';
import { LOGO_DARK, LOGO_LIGHT } from '@unistocks/shared/consts';
import { useIsDarkMode } from '@unistocks/shared/hooks/use-active-color-scheme';
import { color } from '@unistocks/shared/utils/color';
import {
  checkForUpdateAsync,
  fetchUpdateAsync,
  reloadAsync,
  UpdateCheckResult,
  useUpdates
} from 'expo-updates';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'tamagui';

export const useAutomaticUpdates = () => {
  const queryClient = useQueryClient();
  const { isDownloading, downloadProgress } = useUpdates();
  const hasRunUpdateCheck = useRef(false);
  const [didUpdateCheck, setDidUpdateCheck] = useState(false);
  const [loadingText, setLoadingText] = useState<string | undefined>('::Updates:LoadingText');
  const isDarkMode = useIsDarkMode();
  const theme = useTheme();

  useEffect(() => {
    if (hasRunUpdateCheck.current) return;
    hasRunUpdateCheck.current = true;

    (async () => {
      let updateResponse: UpdateCheckResult;
      try {
        updateResponse = await checkForUpdateAsync();
      } catch {
        // Ignore, updates not available in dev mode
        setDidUpdateCheck(true);
        return;
      }

      const { isAvailable } = updateResponse;

      if (isAvailable && !__DEV__) {
        const cssBackground = color(theme.background.val).toCSSHex();
        const logoImage = isDarkMode ? LOGO_DARK : LOGO_LIGHT;

        try {
          setLoadingText('::Updates:Downloading');
          await fetchUpdateAsync();
          queryClient.invalidateQueries();
          await reloadAsync({
            reloadScreenOptions: {
              backgroundColor: cssBackground,
              image: logoImage,
              imageFullScreen: false,
              imageResizeMode: 'center',
              fade: true
            }
          });
        } catch {
          setDidUpdateCheck(true);
        }
      } else {
        setLoadingText(undefined);
        setDidUpdateCheck(true);
      }
    })();
  }, [queryClient, theme, isDarkMode]);

  /* Debug reload screen */
  /* useEffect(() => {
    const cssBackground = color(theme.background.val).toCSSHex();
    showReloadScreen({
      reloadScreenOptions: {
        backgroundColor: cssBackground,
        image: isDarkMode ? LOGO_DARK : LOGO_LIGHT,
        imageFullScreen: false,
        imageResizeMode: 'center',
        fade: true,
        spinner: { enabled: false, color: theme.unistocks.val, size: 'medium' }
      }
    });

    setTimeout(() => {
      hideReloadScreen();
    }, 5000);
  }, [isDarkMode, theme]); */

  return {
    didUpdateCheck,
    loadingText,
    isDownloadingUpdate: isDownloading,
    updateDownloadProgress: downloadProgress
  };
};
